import clsx from 'clsx';
import type {
  Control,
  FieldValues,
  Path,
  RegisterOptions,
} from 'react-hook-form';
import LabelT from '../Label/LabelT';

type CheckboxTogglerProps<T extends FieldValues> = {
  field?: Path<T>;
  control?: Control<T>;
  options?: RegisterOptions<T>;
  label?: string;
  labelClassName?: string;
  id?: string;
  toggleSize: 'base' | 'large' | 'small' | 'xs';
  colorScheme?:
    | 'default'
    | 'alternative'
    | 'disabled'
    | 'orangeBlue'
    | 'red'
    | 'orangeGreen';
} & React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

const toggleSizes = {
  xs: 'w-5 h-2.5 before:w-1.5 before:h-1.5 before:top-[1px] before:left-[2px] peer-checked:before:left-[10px] min-w-[19px]',
  small:
    'w-6 h-3 before:w-2 before:h-2 before:top-[2px] before:left-[3px] peer-checked:before:left-[14px] min-w-[23px]',
  base: 'w-7 h-4 before:w-2.5 before:h-2.5 before:top-[3px] peer-checked:before:left-[14px]',
  large: 'w-8 h-5 before:w-3 before:h-3',
};

const labelSizes = {
  xs: 'text-xs',
  small: 'text-sm',
  base: 'text-base',
  large: 'text-lg',
};

const toggleColors = {
  default:
    'bg-gray-100 peer-checked:bg-primary-base before:bg-gray-600 peer-checked:before:bg-white peer-checked:ring-primary-base ring-2 ring-stone-300',
  alternative:
    'bg-danger peer-checked:bg-success before:bg-white peer-checked:before:bg-white ring-2 ring-stone-100',
  orangeBlue:
    'bg-orange-base peer-checked:bg-primary-base before:bg-white peer-checked:before:bg-white ring-2 ring-stone-100',
  orangeGreen:
    'bg-orange-base peer-checked:bg-green-dark before:bg-white peer-checked:before:bg-white ring-2 ring-stone-100',
  red: 'bg-gray-100 peer-checked:bg-red-base before:bg-red-base peer-checked:before:bg-white ring-2 ring-red-base',
  disabled:
    'bg-gray-200 peer-checked:bg-primary-base before:bg-gray-600 before:opacity-40 peer-checked:before:bg-white ring-2 ring-stone-300',
};

export function CheckboxTogglerT<T extends FieldValues>({
  field,
  control,
  options,
  label,
  id,
  labelClassName,
  toggleSize,
  colorScheme,
  ...restParams
}: CheckboxTogglerProps<T>) {
  const { disabled, ...registerOptions } = options || {};
  const { className, ...rest } = restParams || {};
  const registerObject =
    field && control ? control.register(field, registerOptions) : {};

  // If the checkbox is disabled, we want to change the color scheme to disabled
  if (disabled) {
    colorScheme = 'disabled';
  }

  const inputDefaultStyle = [
    'relative',
    'flex',
    'items-center',
    'justify-start',
    'w-fit',
    'select-none',
    disabled ? 'opacity-50' : '',
    disabled ? 'cursor-default' : 'cursor-pointer',
    disabled ? 'pointer-events-none' : '',
  ];

  // If the className contains a flex-row-reverse class, we want to change the margin of the label, this means that the toggle is on the right side of the label instead of left.
  if (className?.includes('flex-row-reverse')) {
    labelClassName = `${labelClassName} ml-0 mr-3`;
  } else {
    labelClassName = `${labelClassName} ml-3`;
  }

  return (
    <label className={clsx(inputDefaultStyle, className)}>
      <input
        id={id}
        type='checkbox'
        className='sr-only peer'
        {...rest}
        {...registerObject}
      />
      <div
        className={`${toggleColors[colorScheme || 'default']} ${
          toggleSizes[toggleSize]
        } transition-all ease-in-out block relative p-1 rounded-full before:absolute before:p-1 before:rounded-full before:transition-all before:ease-in-out before:duration-300 before:left-1 peer-checked:before:left-4`}
      />
      {label && (
        <LabelT
          text={label}
          htmlFor={id}
          className={`${labelSizes[toggleSize]} ${labelClassName} `}
        />
      )}
    </label>
  );
}

export default CheckboxTogglerT;
